import {
  liveRunDiagnosticsEnabled,
  runLiveRunDiagnostic,
} from "@t3tools/client-runtime/state/threads";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "../../lib/utils";
import { i18n } from "../../i18n";

type MermaidTheme = "light" | "dark";

interface MermaidDiagramProps {
  className?: string;
  code: string;
  fallback: ReactNode;
  isStreaming?: boolean;
  onRenderStateChange?: (state: MermaidRenderResult) => void;
  theme: MermaidTheme;
}

export type MermaidRenderResult =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; svg: string };

interface MermaidRenderState {
  error: boolean;
  key: string;
  svg: string | null;
}

let mermaidRenderQueue = Promise.resolve();

function enqueueMermaidRender(input: {
  code: string;
  id: string;
  theme: MermaidTheme;
}): Promise<string> {
  const render = async () => {
    const { default: mermaid } = await import("mermaid");
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      suppressErrorRendering: true,
      theme: input.theme === "dark" ? "dark" : "default",
      fontFamily: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      flowchart: { useMaxWidth: true },
    });
    const { svg } = await mermaid.render(input.id, input.code);
    return svg;
  };

  const result = mermaidRenderQueue.then(render, render);
  mermaidRenderQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

export function isMermaidFenceLanguage(language: string): boolean {
  return language.toLowerCase() === "mermaid";
}

function MermaidLoadingPlaceholder() {
  return (
    <div className="chat-markdown-mermaid-loading" role="status" aria-live="polite">
      <span>{i18n.t("markdown.mermaid.rendering")}</span>
      <span className="chat-markdown-mermaid-loading-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    </div>
  );
}

export function MermaidDiagram({
  className,
  code,
  fallback,
  isStreaming = false,
  onRenderStateChange,
  theme,
}: MermaidDiagramProps) {
  const reactId = useId();
  const id = useMemo(() => `t3-mermaid-${reactId.replaceAll(":", "")}`, [reactId]);
  const renderKey = `${theme}\u0000${code}`;
  const renderGenerationRef = useRef(0);
  const previousStreamingRef = useRef<boolean | null>(null);
  const [state, setState] = useState<MermaidRenderState>({
    error: false,
    key: "",
    svg: null,
  });

  useEffect(() => {
    runLiveRunDiagnostic("mermaid.mounted", () => ({ componentId: id }));
    return () => runLiveRunDiagnostic("mermaid.unmounted", () => ({ componentId: id }));
  }, [id]);

  useEffect(() => {
    runLiveRunDiagnostic("mermaid.streaming-changed", () => ({
      componentId: id,
      previous: previousStreamingRef.current,
      next: isStreaming,
    }));
    previousStreamingRef.current = isStreaming;
  }, [id, isStreaming]);

  useEffect(() => {
    if (isStreaming) {
      return;
    }

    let active = true;
    const renderGeneration = liveRunDiagnosticsEnabled()
      ? (renderGenerationRef.current += 1)
      : null;
    runLiveRunDiagnostic("mermaid.render-started", () => ({ componentId: id, renderGeneration }));

    void enqueueMermaidRender({ code, id, theme }).then(
      (svg) => {
        if (active) {
          runLiveRunDiagnostic("mermaid.render-ready", () => ({
            componentId: id,
            renderGeneration,
          }));
          setState({ error: false, key: renderKey, svg });
        }
      },
      () => {
        if (active) {
          runLiveRunDiagnostic("mermaid.render-error", () => ({
            componentId: id,
            renderGeneration,
          }));
          setState({ error: true, key: renderKey, svg: null });
        }
      },
    );

    return () => {
      active = false;
    };
  }, [code, id, isStreaming, renderKey, theme]);

  const renderResult = useMemo<MermaidRenderResult>(
    () =>
      isStreaming || state.key !== renderKey
        ? { status: "loading" }
        : state.error
          ? { status: "error" }
          : { status: "ready", svg: state.svg ?? "" },
    [isStreaming, renderKey, state.error, state.key, state.svg],
  );

  useEffect(() => {
    onRenderStateChange?.(renderResult);
  }, [onRenderStateChange, renderResult]);

  if (renderResult.status === "loading") {
    return <MermaidLoadingPlaceholder />;
  }

  if (renderResult.status === "error") {
    return (
      <>
        <div className="chat-markdown-mermaid-error" role="alert">
          {i18n.t("markdown.mermaid.renderError")}
        </div>
        {fallback}
      </>
    );
  }

  return (
    <div
      className={cn("chat-markdown-mermaid chat-markdown-mermaid-enter", className)}
      dangerouslySetInnerHTML={{ __html: renderResult.svg }}
    />
  );
}
